import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BaseWizardService } from '../base.wizard.service';
import {
  AgentDownloadDialogService,
  ApiService,
  CountryListService,
  DevicesService,
  DialogService,
  EntityCacheProvider,
  InputDatastoreService,
  TimeoutWrapperService
} from '@nrwl/common-lib';
import { DatasourceWizardStep } from './datasource.wizard.step';
import { first, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { cloneDeep, findIndex, forEach, get, isEmpty, last } from 'lodash';

export enum DataSource {
  GIGA_STOR = 'GigaStor',
  GIGA_FLOW_SERVER = 'GigaFlowServer',
  GIGA_TEST = 'GigaTestAgent'
}

const stepId = {
  location: 'wizardService.add_datasource.datasource_location',
  review: 'wizardService.add_datasource.datasource_review',
  selection: 'wizardService.add_datasource.datasource_selection',
  definition: 'wizardService.add_datasource.datasource_definition',
  agentDesc: 'wizardService.add_datasource.datasource_agent_desc',
  agentUpgrade: 'wizardService.upgrade_agent.upgrade_agent',
  agent_Desc: 'wizardService.add_agent.datasource_agent_desc',
  agent_review: 'wizardService.add_agent.datasource_review'
};

@Injectable()
export class DatasourceWizardService extends BaseWizardService {
  openNew: boolean;
  state: ActivatedRoute;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentParam: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatGigaTestData: any;
  agentDownloadService: AgentDownloadDialogService;
  closeWizard: BehaviorSubject<boolean>;
  updateDevicesPath = '/vibe/inventory/v3/updateDevices';
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    dataApiService: ApiService,
    state: ActivatedRoute,
    inputDatastoreService: InputDatastoreService,
    countryListService: CountryListService,
    dialogService: DialogService,
    agentDownloadService: AgentDownloadDialogService,
    private devicesService: DevicesService,
    private readonly timeout: TimeoutWrapperService,
    private readonly entityCache: EntityCacheProvider
  ) {
    super(dataApiService, inputDatastoreService, countryListService, dialogService);
    this.state = state;
    this.stepClass = DatasourceWizardStep;
    this.openNew = false;
    this.formatGigaTestData = null;
    this.agentDownloadService = agentDownloadService;
    this.closeWizard = new BehaviorSubject(false);
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  openWizard(
    wizardId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    wizardConfig: any,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
  ): void {
    super.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
    if (this.editMode) {
      this._setOutputForEditMode(defaultParamsById);
      this.currentStep = last(this.steps);
      this.dataUrl = `${this.dataUrl}`;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getParamValue(dataItem: any, paramName: string): any {
    if (!dataItem) {
      return undefined;
    }
    for (const param of dataItem.params ? dataItem.params : []) {
      if (param.name === paramName) {
        return param.value;
      }
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  populateDataForEditMode(item: any): any {
    // Populating required data for device based on wizard creation data structure
    let editInfo = {};
    if (item.dataType === DataSource.GIGA_TEST) {
      const siteId = get(item, 'dataItem.site', 0);
      editInfo = {
        id: item.id,
        type: item.dataType,
        agentType: item.type.name,
        siteId: siteId,
        site: siteId,
        previousSite: siteId,
        datasourceType: {
          id: item.dataType,
          label: item.type.label,
          iconSrc: item.type.icon
        },
        alias: item.dataItem.name,
        name: item.id,
        siteName: item.site.label,
        locationName: item.site.sublabel,
        city: get(item.site, 'location.city', ''),
        state: get(item.site, 'location.state', ''),
        country: get(item.site, 'location.country', '')
      };
    }
    return editInfo;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateParamValue(key: string, value: any, datasourceParams: any): any {
    const index = findIndex(datasourceParams, { name: key });
    const newObj = {
      name: key,
      value: !value ? '' : value
    };
    if (index !== -1) {
      datasourceParams.splice(index, 1, newObj);
    } else {
      datasourceParams.push(newObj);
    }
    return datasourceParams;
  }

  setNextStep(): void {
    if (
      this.currentStep.id === stepId.selection &&
      (this.output.datasourceType.id === DataSource.GIGA_STOR ||
        this.output.datasourceType.id === DataSource.GIGA_FLOW_SERVER)
    ) {
      // skip agent description page for Gigastor and GigaFlow
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this.currentStep = this.steps[this.currentStep.config.index + 2];
    } else if (
      this.currentStep.id === stepId.location &&
      this.output.datasourceType.id === DataSource.GIGA_STOR
    ) {
      // skip 'network' and 'proxy' for Gigastor
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 2]);
      this.currentStep = this.steps[this.currentStep.config.index + 3];
    } else if (
      this.currentStep.id === stepId.definition &&
      this.output.datasourceType.id === DataSource.GIGA_FLOW_SERVER
    ) {
      // skip 'location','network' and 'proxy' for GigaFlow
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 2]);
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 3]);
      this.currentStep = this.steps[this.currentStep.config.index + 4];
    } else if (this.currentStep.id === stepId.agentDesc) {
      // skip datasource definition page for GigaTest
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this.currentStep = this.steps[this.currentStep.config.index + 2];
    } else if (
      this.currentStep.id === stepId.location &&
      this.output.datasourceType.id === DataSource.GIGA_TEST &&
      this.output.agentType &&
      (this.output.agentType.id === 'windows' || this.output.agentType.id === 'hardware')
    ) {
      // skip 'network' and 'proxy' page for hardware agents and windows agents
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 2]);
      this.currentStep = this.steps[this.currentStep.config.index + 3];
    } else if (
      this.currentStep.id === stepId.agent_Desc &&
      this.output.agentType.id === 'windows'
    ) {
      this.currentStep = this.steps[this.currentStep.config.index + 3];
    } else {
      super.setNextStep();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  goToStep(stepName: any): void {
    super.goToStep(stepName);
    //Disable the back btn to prevent datasource type edit
    this.currentStep.disableBackBtn =
      this.currentStep.id === stepId.agentDesc || this.currentStep.id === stepId.definition;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getFormatOutput(output = this.output, format = this.formatGigaTestData): any {
    const formattedOutput = {};
    forEach(format, (param, key) => {
      if (!isEmpty(param)) {
        formattedOutput[key] = this._getFormatOutputItem(output[key], param);
      } else if (key === 'model') {
        const device = this.devicesService.getDeviceById(output['id']);
        formattedOutput[key] = device.getDevice().model;
      } else if (key === 'extension') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const siteList: any = this.entityCache.findEntityCache('site').findById(output.siteId);
        if (siteList) {
          const extensionData = {
            location: {
              address: output.siteId,
              position: {
                lat: siteList.sitesData.location.lat,
                lon: siteList.sitesData.location.lon
              },
              locationAutoGenerated: false
            }
          };
          formattedOutput[key] = extensionData;
        }
      } else {
        formattedOutput[key] = output[key];
      }
    });
    return formattedOutput;
  }

  _setPreviousStep(): void {
    if (
      this.currentStep.id === stepId.review &&
      this.output.datasourceType.id === DataSource.GIGA_STOR
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 3];
    } else if (
      this.currentStep.id === stepId.review &&
      this.output.datasourceType.id === DataSource.GIGA_FLOW_SERVER
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 4];
    } else if (
      this.currentStep.id === stepId.definition &&
      (this.output.datasourceType.id === DataSource.GIGA_STOR ||
        this.output.datasourceType.id === DataSource.GIGA_FLOW_SERVER)
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 2];
    } else if (
      this.currentStep.id === stepId.location &&
      this.output.datasourceType.id === DataSource.GIGA_TEST
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 2];
    } else if (
      this.currentStep.id === stepId.review &&
      this.output.datasourceType.id === DataSource.GIGA_TEST &&
      this.output.agentType &&
      (this.output.agentType.id === 'windows' || this.output.agentType.id === 'hardware')
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 3];
    } else if (
      this.currentStep.id === stepId.agent_review &&
      this.output.agentType.id === 'windows'
    ) {
      this.currentStep = this.steps[this.currentStep.config.index - 3];
    } else {
      super._setPreviousStep();
    }
    this._initStepData(this.currentStep, this.currentStep, this.defaultParamsById);
    //Disable the back btn to prevent datasource type edit
    this.currentStep.disableBackBtn =
      this.currentStep.id === stepId.agentDesc || this.currentStep.id === stepId.definition;
  }

  showExitInsteadOfClose(): boolean {
    return !!(this.isFinishingStep() && (this.requestFailed || this.errorMessage));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  retrySaveRequest(): any {
    this.output = this.lastSubmittedOutput;
    this.currentStep.output.status = '';
    const result = super._saveWizardData();
    result.subscribe(result => {
      if (result) {
        if (result.success && this.output.datasourceType.id === DataSource.GIGA_TEST) {
          this.currentStep.output.status = result.success.status;
          this.requestingForResponse(result.success.jobId);
        }
      }
    });
    return result;
  }

  //eslint-disable-next-line
  onAgentUpgrade(): any {
    this.isLoading = true;
    this.currentStep.onNext();
    this.dirtyFlag = false;
    this.lastSubmittedOutput = cloneDeep(this.output);
    const result = this._saveUpdateWizardData();
    result.subscribe(() => {
      this.isLoading = false;
      this.currentStep = this.finishingStep;
      if (this.output?.model.indexOf('JMEP') !== -1) {
        this.updateJmepSubStatus();
      }
    });
    return result;
  }

  updateJmepSubStatus(): void {
    const selectedAgents = this.output.agents;
    selectedAgents.forEach(deviceId => {
      this.devicesService
        .getDeviceById$(deviceId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(res => {
          const payload = [];
          if (res?.length) {
            const deviceData = res[0];
            const currentTimestamp = new Date().toISOString().substring(0, 19);
            deviceData.substatus = 'upgrade';
            deviceData.lastUpdateStartTime = currentTimestamp;
            deviceData.lastUpgradeMessageTime = currentTimestamp;
            deviceData.lastUpgradeMessage = 'Upgrade Started';
            payload.push(deviceData);
            this.updateManagedDevice(payload).pipe(takeUntil(this.unsubscribe)).subscribe();
          }
        });
    });
  }

  //eslint-disable-next-line
  updateManagedDevice(data): Observable<any> {
    //eslint-disable-next-line
    return this.apiService.post$<any>(`${this.updateDevicesPath}`, data);
  }

  /* eslint-disable-next-line */
  _saveUpdateWizardData(openOnFinish = false): any {
    //Save last step data
    this.currentStep.onNext();
    this.dirtyFlag = false;
    let output: any;
    if (this.output?.model.indexOf('JMEP') !== -1) {
      this.method = 'post';
      this.dataUrl = '/uicomm/upgradeJMEPAsync';
      output = this._getJmepUpgradeFormatOutput(this.output);
    } else {
      output = this._getFormatOutput(this.output, this.formatData);
    }
    return this._saveRequest(output, openOnFinish);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getJmepUpgradeFormatOutput(output = this.output): any {
    const formattedOutput = {};
    formattedOutput['url'] = output.url;
    formattedOutput['fileName'] = output.version;
    formattedOutput['probes'] = output.jmepProbes;

    return JSON.stringify(formattedOutput);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFinish(): any {
    this.currentStep.onNext();
    this.dirtyFlag = false;
    const GigaTestAgentCreate = 'creation' + '/';
    if (
      this.output.datasourceType &&
      this.output.datasourceType.id === DataSource.GIGA_TEST &&
      !this.editMode
    ) {
      this.dataUrl = '/agent/v1/';
      this.dataUrl = this.editMode ? this.dataUrl : this.dataUrl + GigaTestAgentCreate;
    }
    this.lastSubmittedOutput = cloneDeep(this.output);
    const result = this._saveWizardData();
    result.subscribe(result => {
      if (result) {
        this.currentStep = this.finishingStep;
        if (
          result.success &&
          this.output.datasourceType.id === DataSource.GIGA_TEST &&
          !this.editMode
        ) {
          this.currentStep.output.status = result.success.status;
          this.requestingForResponse(result.success.jobId);
        }
      }
    });
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _getDataForEdit(path: any): void {
    this.apiService
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(path, {})
      .pipe(first())
      .subscribe(
        response => {
          this._setOutputForEditMode(response);
          this.defaultParamsById = response;
        },
        error => {
          this._handleErrorResponse(error, this.onFail);
        },
        () => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _handleErrorResponse(response: any, onFail: any): void {
    this.isLoading = false;
    if (response && response.error) {
      if (response.error.errors && get(response, 'error.errors', false)) {
        if (get(response, 'error.errors', false) instanceof Array) {
          this.errorMessage = response.error.errors[0];
        } else {
          this.errorMessage = response.error.errors;
        }
      }
    }

    if (onFail) {
      onFail(response);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  onAgentWizardSuccess(agent: any): void {
    // auto close wizard, open agent dialog
    if (this.wizardActive) {
      this.agentDownloadService.openDialog(agent.id);
      this.closeWizard.next(true);
    }
    this.onSuccess(agent);
  }

  requestingForResponse(jobId: string): void {
    this.isLoading = true;
    const url = this.currentStep.config.def.stateUrl + jobId;
    this.apiService
      .get$(url, {})
      .pipe(first())
      .subscribe(
        response => {
          this.currentStep.output.status = response.success.status;
          switch (this.currentStep.output.status) {
            case 'SUBMITTED':
            case 'BUILDING':
              this._reqResOnSubmittedOrBuilding(jobId);
              break;
            case 'SUCCESSFUL':
              this._reqResOnSuccessful(response);
              this.isLoading = false;
              break;
            case 'FAILED':
              this._reqResOnFailded(response);
              break;
            default:
          }
        },
        () => {},
        () => {}
      );
  }

  _reqResOnSubmittedOrBuilding(jobId: string): void {
    this.timeout.add(() => {
      this.requestingForResponse(jobId);
    }, 500);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _reqResOnFailded(response: any): void {
    this.isLoading = false;
    this.currentStep.output.reason = response.success.reason;
    this._handleErrorResponse(response, this.onFail);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  _reqResOnSuccessful(response: any): void {
    this.onAgentWizardSuccess(response.success.agent);
  }
}
